/**
 * Captures a small thumbnail PNG (data URL) from the live Three.js canvas.
 *
 * The renderer is constructed with `preserveDrawingBuffer: true` (see
 * Mesh2MotionEngine) so `canvas.toDataURL()` returns valid pixel data outside
 * the render call. We downscale via a 2D-canvas blit so the stored thumbnail
 * is small (≈100×120) regardless of viewport size — keeping clip metadata
 * compact in IndexedDB.
 *
 * The captured image is whatever's currently in the viewport. The caller picks
 * the moment to capture (e.g. immediately after a bake completes, when the
 * skeleton is in the just-baked pose).
 */
const THUMB_WIDTH = 100
const THUMB_HEIGHT = 120

export class ClipThumbnailCapture {
  /**
   * Captures the source canvas as a downscaled PNG data URL. Returns `null` if
   * the source canvas has zero dimensions or the 2D context isn't available.
   */
  static capture (source_canvas: HTMLCanvasElement): string | null {
    if (source_canvas.width === 0 || source_canvas.height === 0) return null

    const out = document.createElement('canvas')
    out.width = THUMB_WIDTH
    out.height = THUMB_HEIGHT
    const ctx = out.getContext('2d')
    if (ctx === null) return null

    // Center-crop the source canvas to match the thumbnail's aspect ratio so
    // wide viewports don't squash the figure. Crop to the largest centered
    // rect that has the same aspect as the thumbnail.
    const target_aspect = THUMB_WIDTH / THUMB_HEIGHT
    const source_aspect = source_canvas.width / source_canvas.height

    let sx = 0
    let sy = 0
    let sw = source_canvas.width
    let sh = source_canvas.height
    if (source_aspect > target_aspect) {
      // Source is wider than target — crop horizontally.
      sw = source_canvas.height * target_aspect
      sx = (source_canvas.width - sw) / 2
    } else {
      // Source is taller than target — crop vertically.
      sh = source_canvas.width / target_aspect
      sy = (source_canvas.height - sh) / 2
    }

    try {
      ctx.drawImage(source_canvas, sx, sy, sw, sh, 0, 0, THUMB_WIDTH, THUMB_HEIGHT)
      return out.toDataURL('image/png')
    } catch (err) {
      console.warn('ClipThumbnailCapture failed:', err)
      return null
    }
  }
}
